/**
 * incidentRules — Etapa 6 (P1-2: Fila de Incidentes).
 *
 * Helper PURO (sem React/Leaflet). Deriva a fila de incidentes da lista de
 * devices já carregada (DTO do mapa), SEM nova chamada de rede. Cada device
 * problemático gera UM incidente (o de maior severidade). Renderização
 * defensiva: campos ausentes não geram incidente nem quebram.
 *
 * Watchdog com falha NÃO entra: o DTO atual não expõe flag de falha do
 * watchdog (só host1/host2/mode) — fica como limitação conhecida.
 */

export const INCIDENT_META = {
  offline:   { sev: 0, color: "#FF5063", label: "Offline" },
  relayoff:  { sev: 1, color: "#FF8A3C", label: "Relé OFF" },
  weak:      { sev: 3, color: "#FFB224", label: "RSSI fraco" },
  heartbeat: { sev: 4, color: "#FFB224", label: "Heartbeat atrasado" },
  scorelow:  { sev: 5, color: "#FFB224", label: "Score baixo" },
  ota:       { sev: 6, color: "#60a5fa", label: "OTA com falha" },
};

function lastSeenMs(iso) {
  if (!iso) return 0;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? 0 : t;
}

function make(kind, d) {
  const meta = INCIDENT_META[kind];
  return {
    device_id: d.device_id,
    kind,
    sev: meta.sev,
    color: meta.color,
    reason: meta.label,
    customer: d.customer_name || d.customer_code || null,
    last_seen: d.last_seen || null,
    score: d.health_score ?? null,
    ota_state: d.ota_state || null,
  };
}

/** Retorna o incidente de MAIOR severidade do device, ou null se saudável. */
export function deviceIncident(d, nowMs) {
  if (!d || !d.device_id) return null;
  if (!d.online) return make("offline", d);
  if (d.relay === "off") return make("relayoff", d);
  if (d.weak_signal) return make("weak", d);

  // Heartbeat atrasado: idade do last_seen acima do esperado p/ o HB do device.
  if (d.last_seen) {
    const age = (nowMs - lastSeenMs(d.last_seen)) / 1000;
    const hb = d.advanced_hb_interval && d.advanced_hb_interval > 0
      ? d.advanced_hb_interval
      : 60;
    const threshold = Math.max(120, hb * 2.5);
    if (age > threshold) return make("heartbeat", d);
  }

  if (d.health_score != null && d.health_score < 50) return make("scorelow", d);
  if (d.ota_state === "failed" || d.ota_state === "expired") return make("ota", d);
  return null;
}

/** Constrói a fila ordenada de incidentes. */
export function buildIncidents(devices, nowMs) {
  const now = nowMs || Date.now();
  const out = [];
  for (const d of devices || []) {
    const inc = deviceIncident(d, now);
    if (inc) out.push(inc);
  }
  out.sort((a, b) =>
    a.sev - b.sev ||
    (lastSeenMs(a.last_seen) - lastSeenMs(b.last_seen)) ||
    ((a.score ?? 101) - (b.score ?? 101)),
  );
  return out;
}
